package com.evoapps.evoshift.domain.spreadsheet

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * A minimal .xlsx reader (no Apache POI, the same on every platform): `xl/workbook.xml` (the
 * sheets, their names and order, the 1904 date system), `xl/_rels/workbook.xml.rels` (where each
 * sheet's part is), `xl/sharedStrings.xml`, `xl/styles.xml` (which cells have a date or time
 * format) and `xl/worksheets/sheetN.xml`.
 *
 * Cells: shared and inline strings (rich text runs joined, phonetic hints left out), numbers as
 * stored, booleans as TRUE/FALSE, errors as written (#N/A), formulas as their cached value, and
 * numbers with a date/time format as ISO text ([Sheet]). Not read: colours, fonts, merged cells
 * (their value is in the top-left cell only), comments, charts. Hidden sheets are left out unless
 * every sheet is hidden.
 */
object XlsxReader {

    suspend fun read(bytes: ByteArray, maxRows: Int = SpreadsheetReader.MAX_ROWS): Spreadsheet {
        val zip = ZipReader(bytes)
        if ("xl/workbook.xml" !in zip.names) {
            throw SpreadsheetException(SpreadsheetProblem.NOT_A_SPREADSHEET, "no xl/workbook.xml")
        }
        val workbook = Xml.tags(zip.text("xl/workbook.xml") ?: "")
        val date1904 = workbook.firstOrNull { it.name == "workbookPr" }?.attributes?.get("date1904")?.let { it == "1" || it == "true" } == true
        val rels = zip.text("xl/_rels/workbook.xml.rels")?.let(Xml::tags).orEmpty()
            .filter { it.name == "Relationship" }
            .associate { it.attributes["Id"].orEmpty() to it.attributes["Target"].orEmpty() }
        val declared = workbook.filter { it.name == "sheet" }.mapNotNull { tag ->
            val target = rels[tag.attributes["r:id"]] ?: return@mapNotNull null
            val path = when {
                target.startsWith("/") -> target.removePrefix("/")
                target.startsWith("xl/") -> target
                else -> "xl/$target"
            }
            Triple(tag.attributes["name"].orEmpty(), path, tag.attributes["state"] == "hidden" || tag.attributes["state"] == "veryHidden")
        }.filter { (_, path, _) -> path in zip.names }
        val visible = declared.filterNot { it.third }.ifEmpty { declared }
        val parts = visible.map { it.first to it.second }.ifEmpty {
            // No usable workbook relationships: the worksheets in name order.
            zip.names.filter { it.startsWith("xl/worksheets/") && it.endsWith(".xml") }.sorted().mapIndexed { i, path -> "Sheet${i + 1}" to path }
        }
        if (parts.isEmpty()) throw SpreadsheetException(SpreadsheetProblem.NOT_A_SPREADSHEET, "no worksheet")
        val shared = zip.text("xl/sharedStrings.xml")?.let(::sharedStrings).orEmpty()
        val dateStyles = zip.text("xl/styles.xml")?.let(::dateStyles).orEmpty()
        val cells = CellText(shared, dateStyles, date1904)
        val sheets = parts.map { (name, path) -> Sheet(name, rows(zip.text(path).orEmpty(), cells, maxRows)) }
        return Spreadsheet(SpreadsheetFormat.XLSX, sheets)
    }

    private suspend fun ZipReader.text(name: String): String? = read(name)?.decodeToString()

    // --- Shared strings -----------------------------------------------------------------------

    private fun sharedStrings(xml: String): List<String> {
        val result = mutableListOf<String>()
        var current: StringBuilder? = null
        var inText = false
        var phonetic = 0
        Xml.events(xml) { event ->
            when (event) {
                is Xml.Event.Open -> when (event.tag.name) {
                    "si" -> current = StringBuilder()
                    "rPh" -> if (!event.tag.selfClosing) phonetic++
                    "t" -> inText = !event.tag.selfClosing
                }
                is Xml.Event.Close -> when (event.name) {
                    "si" -> { result += current?.toString().orEmpty(); current = null }
                    "rPh" -> phonetic--
                    "t" -> inText = false
                }
                is Xml.Event.Text -> if (inText && phonetic == 0) current?.append(event.text)
            }
        }
        return result
    }

    // --- Styles: which cell formats show dates and times --------------------------------------

    internal enum class DateKind { DATE, DATE_TIME, TIME }

    /** For each cell style (`<xf>` of `<cellXfs>`, by index), its date kind; null: not a date. */
    private fun dateStyles(xml: String): List<DateKind?> {
        val custom = mutableMapOf<Int, String>()
        val styles = mutableListOf<DateKind?>()
        var inCellXfs = false
        Xml.events(xml) { event ->
            when (event) {
                is Xml.Event.Open -> when (event.tag.name) {
                    "numFmt" -> {
                        val id = event.tag.attributes["numFmtId"]?.toIntOrNull()
                        if (id != null) custom[id] = event.tag.attributes["formatCode"].orEmpty()
                    }
                    "cellXfs" -> inCellXfs = !event.tag.selfClosing
                    "xf" -> if (inCellXfs) {
                        val id = event.tag.attributes["numFmtId"]?.toIntOrNull() ?: 0
                        styles += custom[id]?.let(::dateKindOfFormat) ?: builtInDateKind(id)
                    }
                }
                is Xml.Event.Close -> if (event.name == "cellXfs") inCellXfs = false
                is Xml.Event.Text -> Unit
            }
        }
        return styles
    }

    /** Excel's built-in number formats that show dates or times (ECMA-376, 18.8.30). */
    internal fun builtInDateKind(id: Int): DateKind? = when (id) {
        14, 15, 16, 17, in 27..31, in 34..36, in 50..58 -> DateKind.DATE
        22 -> DateKind.DATE_TIME
        18, 19, 20, 21, 32, 33, 45, 46, 47 -> DateKind.TIME
        else -> null
    }

    /**
     * The date kind of a custom format code ("dd.mm.yyyy", "yyyy-mm-dd hh:mm", "[h]:mm"), or null
     * for a number format. Quoted text, escaped characters and colour/condition sections ("[Red]")
     * are not format letters; only the first section (positive numbers) counts.
     */
    internal fun dateKindOfFormat(code: String): DateKind? {
        val letters = StringBuilder()
        var i = 0
        while (i < code.length) {
            val c = code[i]
            when {
                c == ';' -> break
                c == '"' -> { val end = code.indexOf('"', i + 1); i = if (end < 0) code.length else end }
                c == '\\' || c == '_' || c == '*' -> i++
                c == '[' -> {
                    val end = code.indexOf(']', i + 1).let { if (it < 0) code.length else it }
                    val inside = code.substring(i + 1, end).lowercase()
                    // [h], [mm], [ss]: elapsed time; anything else ([Red], [$-415], [>100]) is not a letter.
                    if (inside.isNotEmpty() && inside.all { it == 'h' || it == 'm' || it == 's' }) letters.append(inside)
                    i = end
                }
                else -> letters.append(c.lowercaseChar())
            }
            i++
        }
        val text = letters.toString()
        if ("general" in text) return null
        // "m" alone is a month only without hours or seconds next to it ("mm:ss" is a time).
        val hasDate = 'y' in text || 'd' in text || ('m' in text && 'h' !in text && 's' !in text && '0' !in text && '#' !in text)
        val hasTime = 'h' in text || 's' in text
        return when {
            hasDate && hasTime -> DateKind.DATE_TIME
            hasDate -> DateKind.DATE
            hasTime -> DateKind.TIME
            else -> null
        }
    }

    // --- Cells ----------------------------------------------------------------------------------

    /** Turns a cell's stored value into its text ([Sheet]). */
    private class CellText(val shared: List<String>, val dateStyles: List<DateKind?>, date1904: Boolean) {
        private val epoch = if (date1904) LocalDate(1904, 1, 1) else LocalDate(1899, 12, 30)

        fun of(type: String?, style: Int?, raw: String, inline: String?): String = when (type) {
            "s" -> raw.trim().toIntOrNull()?.let { shared.getOrNull(it) }.orEmpty()
            "inlineStr" -> inline ?: raw
            "b" -> if (raw.trim() == "1") "TRUE" else "FALSE"
            "str", "e" -> raw
            // An ISO date cell (t="d", rare): its date (and time) as written.
            "d" -> raw.trim().replace('T', ' ').removeSuffix("Z").let { if (it.endsWith(" 00:00:00")) it.take(10) else it.take(16) }
            else -> {
                val kind = style?.let { dateStyles.getOrNull(it) }
                val number = raw.trim()
                if (kind == null || number.isEmpty()) number else serialToText(number, kind) ?: number
            }
        }

        /** Excel's day number (with a fraction for the time) as "2026-02-01", "2026-02-01 08:30" or "08:30". */
        fun serialToText(number: String, kind: DateKind): String? {
            val value = number.toDoubleOrNull()?.takeIf { it.isFinite() && it >= 0 && it < 2_958_466 } ?: return null
            var days = kotlin.math.floor(value).toLong()
            var minutes = kotlin.math.round((value - days) * 24 * 60).toLong()
            if (minutes >= 24 * 60) { days += 1; minutes -= 24 * 60 }
            val time = "${(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}"
            if (kind == DateKind.TIME) return time
            val date = epoch.plus(DatePeriod(days = days.toInt())).toString()
            return if (kind == DateKind.DATE_TIME && minutes != 0L) "$date $time" else date
        }
    }

    private fun rows(xml: String, cells: CellText, maxRows: Int): List<List<String>> {
        val rows = mutableMapOf<Int, MutableMap<Int, String>>()
        var rowIndex = -1
        var column = 0
        var nextColumn = 0
        var type: String? = null
        var style: Int? = null
        var value: StringBuilder? = null
        var inline: StringBuilder? = null
        var inValue = false
        var inInlineText = false
        var inFormula = false
        var phonetic = 0
        var tooMany = false
        Xml.events(xml) { event ->
            if (tooMany) return@events
            when (event) {
                is Xml.Event.Open -> when (event.tag.name) {
                    "row" -> {
                        rowIndex = event.tag.attributes["r"]?.toIntOrNull()?.minus(1) ?: (rowIndex + 1)
                        nextColumn = 0
                    }
                    "c" -> {
                        column = event.tag.attributes["r"]?.let(::columnOf) ?: nextColumn
                        nextColumn = column + 1
                        type = event.tag.attributes["t"]
                        style = event.tag.attributes["s"]?.toIntOrNull()
                        value = StringBuilder()
                        inline = null
                        if (event.tag.selfClosing) value = null
                    }
                    "v" -> inValue = !event.tag.selfClosing
                    "f" -> inFormula = !event.tag.selfClosing
                    "is" -> inline = StringBuilder()
                    "rPh" -> if (!event.tag.selfClosing) phonetic++
                    "t" -> inInlineText = inline != null && !event.tag.selfClosing
                }
                is Xml.Event.Close -> when (event.name) {
                    "v" -> inValue = false
                    "f" -> inFormula = false
                    "t" -> inInlineText = false
                    "rPh" -> phonetic--
                    "c" -> {
                        val raw = value?.toString()
                        if (raw != null && rowIndex >= 0) {
                            val text = cells.of(type, style, raw, inline?.toString())
                            // Formatted but empty rows far below the data do not count; a value there does.
                            if (text.isNotEmpty() && rowIndex >= maxRows) tooMany = true
                            else if (text.isNotEmpty()) rows.getOrPut(rowIndex) { mutableMapOf() }[column] = text
                        }
                        value = null
                        inline = null
                    }
                }
                is Xml.Event.Text -> when {
                    inFormula -> Unit
                    inValue -> value?.append(event.text)
                    inInlineText && phonetic == 0 -> inline?.append(event.text)
                }
            }
        }
        if (tooMany) throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE, "more than $maxRows rows")
        // Rows the sheet leaves out are empty rows, so row numbers stay the spreadsheet's.
        val last = (rows.keys.maxOrNull() ?: -1) + 1
        return List(last) { index ->
            val row = rows[index].orEmpty()
            val width = (row.keys.maxOrNull() ?: -1) + 1
            List(width) { row[it].orEmpty() }
        }
    }

    /** "B12" → 1 (column B, zero-based). */
    fun columnOf(reference: String): Int? {
        var column = 0
        var letters = 0
        for (c in reference) {
            if (c in 'A'..'Z') { column = column * 26 + (c - 'A' + 1); letters++ } else break
        }
        return if (letters == 0 || letters > 3) null else column - 1
    }

    /** 1 → "B" (zero-based column to its letters). */
    fun columnName(index: Int): String {
        var n = index + 1
        val out = StringBuilder()
        while (n > 0) {
            val r = (n - 1) % 26
            out.insert(0, 'A' + r)
            n = (n - 1) / 26
        }
        return out.toString()
    }
}

/** A minimal XML reader for the spreadsheet parts: tags with attributes, text, entities. No DTDs. */
internal object Xml {
    class Tag(val name: String, val attributes: Map<String, String>, val selfClosing: Boolean)

    sealed interface Event {
        class Open(val tag: Tag) : Event
        class Close(val name: String) : Event
        class Text(val text: String) : Event
    }

    fun tags(xml: String): List<Tag> = buildList { events(xml) { if (it is Event.Open) add(it.tag) } }

    fun events(xml: String, onEvent: (Event) -> Unit) {
        var i = 0
        val n = xml.length
        while (i < n) {
            val lt = xml.indexOf('<', i)
            val textEnd = if (lt < 0) n else lt
            if (textEnd > i) onEvent(Event.Text(decode(xml.substring(i, textEnd))))
            if (lt < 0) return
            when {
                xml.startsWith("<!--", lt) -> { i = xml.indexOf("-->", lt).let { if (it < 0) n else it + 3 }; continue }
                xml.startsWith("<![CDATA[", lt) -> {
                    val end = xml.indexOf("]]>", lt).let { if (it < 0) n else it }
                    onEvent(Event.Text(xml.substring(lt + 9, end)))
                    i = if (end >= n) n else end + 3
                    continue
                }
                // Declarations and DTDs are skipped: no entity is ever expanded from them.
                xml.startsWith("<?", lt) || xml.startsWith("<!", lt) -> { i = xml.indexOf('>', lt).let { if (it < 0) n else it + 1 }; continue }
            }
            val gt = endOfTag(xml, lt)
            if (gt < 0) return
            val body = xml.substring(lt + 1, gt)
            if (body.startsWith("/")) {
                onEvent(Event.Close(localName(body.substring(1).trim())))
            } else {
                val selfClosing = body.endsWith("/")
                val content = if (selfClosing) body.dropLast(1) else body
                val nameEnd = content.indexOfFirst { it.isWhitespace() }.let { if (it < 0) content.length else it }
                val name = localName(content.substring(0, nameEnd))
                val tag = Tag(name, attributes(content.substring(nameEnd)), selfClosing)
                onEvent(Event.Open(tag))
                if (selfClosing) onEvent(Event.Close(name))
            }
            i = gt + 1
        }
    }

    /** The '>' closing the tag at [start], skipping '>' inside quoted attribute values. */
    private fun endOfTag(xml: String, start: Int): Int {
        var quote: Char? = null
        for (j in start + 1 until xml.length) {
            val c = xml[j]
            if (quote != null) { if (c == quote) quote = null } else if (c == '"' || c == '\'') quote = c else if (c == '>') return j
        }
        return -1
    }

    /** "x:sheet" → "sheet"; relationship ids keep their prefix as an attribute ("r:id"). */
    private fun localName(name: String): String = name.substringAfter(':')

    private fun attributes(text: String): Map<String, String> {
        val result = mutableMapOf<String, String>()
        var i = 0
        while (i < text.length) {
            while (i < text.length && text[i].isWhitespace()) i++
            val eq = text.indexOf('=', i)
            if (eq < 0) break
            val key = text.substring(i, eq).trim()
            var q = eq + 1
            while (q < text.length && text[q].isWhitespace()) q++
            if (q >= text.length) break
            val quote = text[q]
            if (quote != '"' && quote != '\'') break
            val close = text.indexOf(quote, q + 1)
            if (close < 0) break
            result[key] = decode(text.substring(q + 1, close))
            i = close + 1
        }
        return result
    }

    fun decode(text: String): String {
        if ('&' !in text) return text
        val out = StringBuilder(text.length)
        var i = 0
        while (i < text.length) {
            val c = text[i]
            val semi = if (c == '&') text.indexOf(';', i) else -1
            if (semi < 0 || semi - i > 10) { out.append(c); i++; continue }
            val entity = text.substring(i + 1, semi)
            val decoded: String? = when {
                entity == "amp" -> "&"
                entity == "lt" -> "<"
                entity == "gt" -> ">"
                entity == "quot" -> "\""
                entity == "apos" -> "'"
                entity.startsWith("#x") || entity.startsWith("#X") -> entity.substring(2).toIntOrNull(16)?.let(::codePoint)
                entity.startsWith("#") -> entity.substring(1).toIntOrNull()?.let(::codePoint)
                else -> null
            }
            if (decoded == null) { out.append(c); i++ } else { out.append(decoded); i = semi + 1 }
        }
        return out.toString()
    }

    private fun codePoint(value: Int): String? = when {
        value < 0 || value > 0x10FFFF -> null
        value < 0x10000 -> value.toChar().toString()
        else -> {
            val v = value - 0x10000
            charArrayOf((0xD800 + (v shr 10)).toChar(), (0xDC00 + (v and 0x3FF)).toChar()).concatToString()
        }
    }
}
